import { EProcessState, EProgressStatus } from '@/models/enums';
import { useMemo } from 'react';
import { gql, TypedDocumentNode, useQuery } from '@apollo/client';
import { useUserContext } from '@/contexts/UserContext';
import {
  ContentGroupType,
  ContentPageCompletionType,
  ContentPageType,
  Maybe,
  TagType,
  UserFlowStepType,
  UserFlowType,
} from '@netfront/api-ekardo-types';
import { useEkardoClient } from '@/components/Providers/ApolloClientsProvider';
import { PickIfNotNull, PickWithExtension } from '@/utils/types';
import { environmentVariables } from '@/utils/environmentVariables';
import { isProductionHost } from '@/utils/environment';
import { isComingSoon, isContentGroupVisible, filterVisibleContentPages } from '@/utils/tags';

type UseModulesQueryResponse = {
  userFlow: {
    getUserFlowByUrl: PickWithExtension<
      UserFlowType,
      'id',
      {
        userFlowSteps: Array<
          PickWithExtension<
            UserFlowStepType,
            'id' | 'isComplete' | 'lastViewedContentPageCompletion' | 'percentageCompleted' | 'sort' | 'url',
            {
              contentGroup: PickWithExtension<
                ContentGroupType,
                'sort' | 'title' | 'url' | 'id' | 'status',
                {
                  contentPages: Pick<ContentPageType, 'url' | 'id' | 'parentId' | 'sort' | 'status'>[];
                  iconAsset: PickIfNotNull<ContentGroupType['iconAsset'], 'id' | 'presignedUrl'>;
                  tags: Pick<TagType, 'id' | 'name' | 'sort'>[];
                }
              >;
            }
          >
        >;
      }
    >;
  };
};

type UseModulesQueryVariables = {
  projectId: string;
  url: string;
};

/**
 * `contentPageStatuses` is baked in as a literal (not a GraphQL variable) since the
 * exact enum type name for this argument isn't known outside the live schema. Draft
 * pages are only requested off the production host - see isProductionHost() below -
 * so production always fetches PUBLISHED only and never receives draft page data.
 */
function buildUseModulesQuery(contentPageStatuses: string): TypedDocumentNode<UseModulesQueryResponse, UseModulesQueryVariables> {
  return gql(`
    query getUserFlowByUrl($projectId: String!, $url: String!) {
      userFlow {
        getUserFlowByUrl(projectId: $projectId, url: $url) {
          id
          userFlowSteps {
            contentGroup {
              id
              status
              contentPages(status: [${contentPageStatuses}]) {
                url
                id
                parentId
                sort
                status
              }
              sort
              title
              url
              iconAsset {
                id
                presignedUrl
              }
              tags {
                id
                name
                sort
              }
            }
            id
            isComplete
            lastViewedContentPageCompletion {
              contentPage {
                url
              }
            }
            percentageCompleted
            sort
            url
          }
        }
      }
    }
  `);
}

export const USE_MODULES_QUERY_PUBLISHED_ONLY = buildUseModulesQuery('PUBLISHED');
export const USE_MODULES_QUERY_WITH_DRAFTS = buildUseModulesQuery('PUBLISHED, UNPUBLISHED');

type ModuleCardInfo = {
  stepId: number;
  href: string;
  percentageComplete: number;
  status: EProgressStatus;
  title: string;
  iconUrl: string;
  iconId: string;
};

function ensurePercentageCompletedIsNot100IfNotComplete(percentageCompleted: number, isComplete: boolean): number {
  if (!isComplete && percentageCompleted >= 100) {
    return 99;
  }

  return percentageCompleted;
}

export default function useModules() {
  const { user } = useUserContext();
  const query = useQuery<UseModulesQueryResponse, UseModulesQueryVariables>(
    isProductionHost() ? USE_MODULES_QUERY_PUBLISHED_ONLY : USE_MODULES_QUERY_WITH_DRAFTS,
    {
      fetchPolicy: 'cache-and-network',
      client: useEkardoClient(),
      variables: {
        projectId: environmentVariables.projectId,
        url: 'modules',
      },
      skip: !user,
    },
  );
  const { data, loading, error } = query;

  const modules = (data?.userFlow?.getUserFlowByUrl?.userFlowSteps || [])
    .filter(({ contentGroup }) => isContentGroupVisible(contentGroup))
    .toSorted((a, b) => a.sort - b.sort || a.id - b.id);
  const completableModules = modules.filter(({ contentGroup }) => !isComingSoon(contentGroup.tags));
  const totalPercentageCompleted = completableModules.length
    ? (completableModules.filter(({ isComplete }) => isComplete).length / completableModules.length) * 100
    : 0;

  const moduleCards = useMemo<ModuleCardInfo[]>(() => {
    if (!modules) {
      return [];
    }

    return modules.map((step, index): ModuleCardInfo => {
      const { contentGroup, id: stepId, isComplete, percentageCompleted, url, lastViewedContentPageCompletion } = step;

      const isLocked = false; //Add linear locking

      const contentPageUrl = computeModuleLinkBasedOnProgress(
        lastViewedContentPageCompletion,
        { ...contentGroup, contentPages: filterVisibleContentPages(contentGroup.contentPages, contentGroup.tags) },
        isComplete,
      );

      const status = isComingSoon(contentGroup.tags)
        ? EProgressStatus.ComingSoon
        : isComplete
        ? EProgressStatus.Completed
        : isLocked
        ? EProgressStatus.Locked
        : percentageCompleted === 0
        ? EProgressStatus.NotStarted
        : EProgressStatus.InProgress;

      return {
        stepId,
        href: `/console/modules/${url}/${contentPageUrl}`,
        percentageComplete: ensurePercentageCompletedIsNot100IfNotComplete(Math.round(percentageCompleted * 100), isComplete),
        status,
        title: contentGroup.title ?? 'NO TITLE',
        iconUrl: contentGroup.iconAsset?.presignedUrl || '',
        iconId: contentGroup.iconAsset?.id ?? '',
      };
    });
  }, [modules]);

  const firstIncompleteModule = useMemo<
    | {
        state: 'loading';
      }
    | {
        state: 'hasIncomplete';
        module: ModuleCardInfo;
      }
    | {
        state: 'allCompleted';
      }
  >(() => {
    if (loading && !data) {
      return {
        state: 'loading',
      };
    }

    if (moduleCards.length === 0) {
      return {
        state: 'allCompleted',
      };
    }

    const incompleteModule = moduleCards.find(
      (module) => module.status !== EProgressStatus.Completed && module.status !== EProgressStatus.ComingSoon,
    );
    if (incompleteModule) {
      return {
        state: 'hasIncomplete',
        module: incompleteModule,
      };
    }

    return {
      state: 'allCompleted',
    };
  }, [moduleCards, loading, data]);

  const fetchState = useMemo<EProcessState>(() => {
    if (!data && loading) {
      return EProcessState.Loading;
    }

    if (error) {
      return EProcessState.Error;
    }

    return EProcessState.Completed;
  }, [loading, data, error]);

  return {
    fetchState,
    query,
    moduleCards,
    percentageCompleted: totalPercentageCompleted,
    firstIncompleteModule,
  };
}

function computeModuleLinkBasedOnProgress(
  lastViewedContentPageCompletion: Maybe<ContentPageCompletionType> | undefined,
  contentGroup: PickWithExtension<
    ContentGroupType,
    'url' | 'id' | 'sort' | 'title',
    {
      contentPages: Pick<ContentPageType, 'url' | 'id' | 'parentId' | 'sort'>[];
      iconAsset: PickIfNotNull<ContentGroupType['iconAsset'], 'id' | 'presignedUrl'>;
    }
  >,
  isComplete: boolean,
) {
  const firstPageUrl = contentGroup.contentPages.find(({ sort, parentId }) => sort === 0 && !parentId)?.url;

  if (!lastViewedContentPageCompletion) {
    return firstPageUrl;
  }

  const parentPages = contentGroup.contentPages.filter(({ parentId }) => !parentId);

  if (isComplete && parentPages.length > 0) {
    const lastParent = parentPages.reduce((max, page) => (page.sort > max.sort ? page : max));
    const children = contentGroup.contentPages.filter(({ parentId }) => parentId === lastParent.id);
    const lastPage = children.length ? children.reduce((max, page) => (page.sort > max.sort ? page : max)) : lastParent;

    if (lastViewedContentPageCompletion.contentPage.url === lastPage.url) {
      return firstPageUrl;
    }
  }

  return lastViewedContentPageCompletion.contentPage.url;
}
